import { ConfigItem, SearchResult } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

import { User } from './User'

export enum ContactsSearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByEmail = 'searchByEmail',
	searchByEmployer = 'searchByEmployer',
	searchByUsername = 'searchByUsername'
}

export enum ContactsFilterFields {
	includeInactive = 'includeInactive',
	billingAgencies = 'billingAgencies',
	startDate = 'startDate',
	endDate = 'endDate'
}

export declare namespace Contact {
	interface ActiveCompany extends Entity {
		suppliers: Nullable<Entity[]>
	}

	interface UserType extends Entity {
		userType: number
	}
	interface Basic extends Entity {
		isActive: boolean
		employer: string | null
		user: User.DefaultUser | null
		email: string | null
	}

	interface Details extends Contact.Form.Create.Request, Contact.AdditionalData {
		userId: string | null
	}

	interface Full {
		contact: DetailsContact & AdditionalData
		companies: Company.Basic[]
		customers: Customer.Basic[]
		serviceSites: ServiceSites.Basic[]
		supplierLocations: SupplierLocations.Basic[]
		companyHierarchy: CompanyHierarchy.Basic[]
	}

	interface DetailsContact {
		mi: string | null
		fax: string | null
		email: string
		mobile: string | null
		office: string | null
		lastName: string
		employer: string | null
		isActive?: boolean
		firstName: string
		emergency: string | null
		billingAgencyIds: number[]
	}

	interface AdditionalData {
		userName: string | null
		lastLogin: string | null
	}

	namespace Form {
		namespace Create {
			interface Request extends Contact.DetailsContact {
				prefix: string | null
				suffix: string | null
				mailCode: string | null
				pin: string | null
				jobTitle: string | null
				internalEmployee: string | null
				preferredLanguageId: number
			}

			interface Basic extends Omit<Request, 'billingAgencyIds'> {
				billingAgencies?: LabelValue<number>[]
			}
		}
	}

	namespace Search {
		interface Result extends Entity {
			email: BasicString
			phone: BasicString
			user: Nullable<UserType>
		}

		namespace Suggestions {
			interface Result extends SearchResult {
				[ContactsSearchResultFields.searchByEmail]: Entity[]
				[ContactsSearchResultFields.searchByEmployer]: Entity['name'][]
				[ContactsSearchResultFields.searchByUsername]: Entity[]
			}

			interface Config {
				[ContactsSearchResultFields.searchById]: ConfigItem<ContactsSearchResultFields>
				[ContactsSearchResultFields.searchByName]: ConfigItem<ContactsSearchResultFields>
				[ContactsSearchResultFields.searchByEmail]: ConfigItem<ContactsSearchResultFields>
				[ContactsSearchResultFields.searchByEmployer]: ConfigItem<ContactsSearchResultFields>
				[ContactsSearchResultFields.searchByUsername]: ConfigItem<ContactsSearchResultFields>
			}
		}
	}

	namespace Filter {
		interface Config {
			[ContactsFilterFields.includeInactive]: ConfigFilterItem<string, boolean>
			[ContactsFilterFields.billingAgencies]: ConfigFilterItem<string, string>
			[ContactsFilterFields.startDate]: ConfigFilterItem<string, null>
			[ContactsFilterFields.endDate]: ConfigFilterItem<string, null>
		}
	}

	namespace Assignment {
		interface Basic {
			company: Omit<Company.Basic, 'isActive'>
		}

		interface Company extends Basic {
			isActive: boolean
		}

		namespace Customer {
			interface Contact {
				customer: Omit<Contact.Customer.Basic, 'isActive'>
				isActive: boolean
			}
			interface CustomerBasic extends Basic {
				customerContacts: Contact[]
			}
			interface Formated extends Contact, Basic {}
		}

		namespace ServiceSite {
			interface Contact {
				serviceSite: Omit<Contact.Customer.Basic, 'isActive'>
				isActive: boolean
			}
			interface ServiceSiteBasic extends Basic {
				serviceSiteContacts: Contact[]
			}
			interface Formated extends Contact, Basic {}
		}

		namespace SupplierLocation {
			interface Contact {
				supplierLocation: Omit<Contact.Customer.Basic, 'isActive'>
				isActive: boolean
			}
			interface SupplierLocationBasic {
				supplier: Omit<Contact.Company.Basic, 'isActive'>
				supplierLocationContacts: Contact[]
			}
			interface Formated extends Contact {
				supplier: Omit<Contact.Company.Basic, 'isActive'>
			}
		}

		type BasicsContacts = ServiceSite.ServiceSiteBasic &
			Customer.CustomerBasic &
			SupplierLocation.SupplierLocationBasic
		type ContactFields = ServiceSite.Contact | Customer.Contact | SupplierLocation.Contact
		type BasicFormatted = ServiceSite.Formated | Customer.Formated | SupplierLocation.Formated
	}

	namespace Company {
		interface Basic extends Entity {
			isActive: boolean
		}
	}

	namespace Customer {
		interface Basic extends Entity {
			isActive: boolean
		}
	}

	namespace ServiceSites {
		interface Basic {
			id: Key
			isActive: boolean
			serviceSiteName: string
		}
	}

	namespace SupplierLocations {
		interface Basic {
			id: Key
			supplier: string
			location: string
			isActive: boolean
		}
	}

	namespace CompanyHierarchy {
		interface Basic {
			id: Key
			isActive: boolean
			parentCompany: string
			organizationalUnit: string
		}
	}

	namespace AutomaticallyAvailableReports {
		interface Basic {
			id: number
			name: string
			description: string
		}
	}
}

export interface CreateContactFormData extends Contact.Form.Create.Basic {}
